import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './OpenSlotControls.css';

const LIVE_STATUSES = ['LIVE', 'STARTED'];
const POLL_MS = 8000;

/**
 * Backfill control for a participant on the live "Anslut" screen.
 *
 * When a player has to leave a live 5v5, a participant opens a slot and names
 * who left (a "vem lämnade?" picker). That choice fixes the substitute's team
 * (right CS + voice team) and the struck-through leaver on the scoreboard. The
 * slot then surfaces on the "Öppna platser" page where any active member can
 * claim it. Auto-detecting the leaver from live connections is the logaddress
 * automation tracked in the backlog — this is the manual flow.
 *
 * The component is a plain participant tool: opening or cancelling a slot
 * grants no match-control rights (the backend enforces participant-only and
 * maps every rule to an HTTP status we surface inline).
 *
 * @param {object} props
 * @param {object} props.match  Match object (id, status, players_per_team, team1/team2 with players).
 */
export default function OpenSlotControls({ match }) {
  const { t } = useTranslation();
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [slots, setSlots] = useState([]);
  const [leaverId, setLeaverId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [paused, setPaused] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(false);

  const matchId = match?.id;
  const eligible =
    !!matchId &&
    match?.players_per_team === 5 &&
    LIVE_STATUSES.includes(match?.status);

  const teams = [match?.team1, match?.team2].filter(Boolean);

  const refresh = useCallback(async () => {
    if (!eligible) return;
    try {
      const res = await fetch(`${API_URL}/api/open-slots/`, {
        headers: { ...getAuthHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        const rows = Array.isArray(data) ? data : data.results || [];
        setSlots(rows.filter((s) => s.match?.id === matchId));
      }
      const psRes = await fetch(
        `${API_URL}/api/open-slots/pause-state/?match_id=${matchId}`,
        { headers: { ...getAuthHeaders() } }
      );
      if (psRes.ok) {
        const ps = await psRes.json();
        setPaused(!!ps.paused);
      }
    } catch (err) {
      // Polling is best-effort — keep the last known state on a blip.
    }
  }, [eligible, matchId, getAuthHeaders]);

  useEffect(() => {
    if (!eligible) return undefined;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [eligible, refresh]);

  const handleOpen = useCallback(async () => {
    if (!leaverId) {
      setError(t('openSlots.selectLeaverError'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/open-slots/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ match_id: matchId, leaver_id: leaverId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.detail?.error || data?.error || t('openSlots.openError'));
      } else {
        setLeaverId('');
        await refresh();
      }
    } catch (err) {
      setError(t('openSlots.networkError'));
    } finally {
      setBusy(false);
    }
  }, [matchId, leaverId, getAuthHeaders, refresh, t]);

  const handleCancel = useCallback(
    async (slotId) => {
      setBusy(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/api/open-slots/${slotId}/cancel/`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data?.detail?.error || data?.error || t('openSlots.cancelError'));
        } else {
          await refresh();
        }
      } catch (err) {
        setError(t('openSlots.networkError'));
      } finally {
        setBusy(false);
      }
    },
    [getAuthHeaders, refresh, t]
  );

  const handlePauseToggle = useCallback(
    async (next) => {
      setPauseBusy(true);
      setError('');
      try {
        const res = await fetch(
          `${API_URL}/api/open-slots/${next ? 'pause' : 'resume'}/`,
          {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ match_id: matchId }),
          }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data?.detail?.error || data?.error || t('openSlots.pauseError'));
        } else {
          setPaused(next);
          await refresh();
        }
      } catch (err) {
        setError(t('openSlots.networkError'));
      } finally {
        setPauseBusy(false);
      }
    },
    [matchId, getAuthHeaders, refresh, t]
  );

  if (!eligible || !isLoggedIn) return null;

  const openSlots = slots.filter((s) => s.status === 'open' || s.status === 'claimed');

  const playerLabel = (p) =>
    p.display_name || p.steam_user?.username || p.discord_user?.username || 'Spelare';

  return (
    <div className="open-slot-controls">
      <div className="open-slot-backfill">
        <h3 className="open-slot-controls-title">{t('openSlots.needPlayerTitle')}</h3>
        <p className="open-slot-controls-hint">{t('openSlots.needPlayerHint')}</p>

        {openSlots.length > 0 ? (
          <ul className="open-slot-list">
            {openSlots.map((slot) => (
              <li key={slot.id} className="open-slot-item">
                <span className="open-slot-team">{slot.team_name}</span>
                {slot.replaced_player_name && (
                  <span className="open-slot-replacing">
                    {t('openSlots.replacing', { name: slot.replaced_player_name })}
                  </span>
                )}
                <span className={`open-slot-status status-${slot.status}`}>
                  {slot.status === 'claimed'
                    ? t('openSlots.incoming')
                    : t('openSlots.waiting')}
                </span>
                {slot.status === 'open' && (
                  <button
                    type="button"
                    className="open-slot-cancel-btn"
                    onClick={() => handleCancel(slot.id)}
                    disabled={busy}
                  >
                    {t('openSlots.cancel')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="open-slot-open-row">
            <select
              className="open-slot-leaver-select"
              value={leaverId}
              onChange={(e) => setLeaverId(e.target.value)}
              disabled={busy}
            >
              <option value="">{t('openSlots.selectLeaver')}</option>
              {teams.map((team) => (
                <optgroup key={team.id} label={team.name}>
                  {(team.players || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {playerLabel(p)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button
              type="button"
              className="open-slot-open-btn"
              onClick={handleOpen}
              disabled={busy || !leaverId}
            >
              {busy ? t('openSlots.opening') : t('openSlots.openSlotBtn')}
            </button>
          </div>
        )}

        {paused ? (
          <div className="open-slot-paused">
            <h3 className="open-slot-controls-title">{t('openSlots.pausedTitle')}</h3>
            <p className="open-slot-controls-hint">{t('openSlots.pausedHint')}</p>
            <button
              type="button"
              className="open-slot-resume-btn"
              onClick={() => handlePauseToggle(false)}
              disabled={pauseBusy}
            >
              {pauseBusy ? t('openSlots.resuming') : t('openSlots.resumeBtn')}
            </button>
          </div>
        ) : (
          <div className="open-slot-pause-row">
            <span className="open-slot-pause-hint">{t('openSlots.pauseHint')}</span>
            <button
              type="button"
              className="open-slot-pause-btn"
              onClick={() => handlePauseToggle(true)}
              disabled={pauseBusy}
            >
              {pauseBusy ? t('openSlots.pausing') : t('openSlots.pauseBtn')}
            </button>
          </div>
        )}
      </div>

      {error && <p className="open-slot-error">{error}</p>}
    </div>
  );
}
